import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { WmPart } from "../lib/wmParts";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtCur, fmtRate, fmtNum, fmtDate, fmtDateTime } from "../lib/format";
import { ArrowLeft, FileText, Truck, ClockCounterClockwise, PencilSimple, Check, X } from "@phosphor-icons/react";

const isoToInput = (v) => {
  if (!v) return "";
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};

export default function MsPODetail() {
  const { pono } = useParams();
  const [po, setPo] = useState(null);
  const [tab, setTab] = useState("header");
  const [editingLine, setEditingLine] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [aliasMap, setAliasMap] = useState({});            // WRONG(UPPER) -> canon
  const [editingItem, setEditingItem] = useState(null);    // line index whose item code is being resolved
  const [itemQuery, setItemQuery] = useState("");
  const [itemResults, setItemResults] = useState([]);
  const [itemSearching, setItemSearching] = useState(false);
  const [savingItem, setSavingItem] = useState(false);

  const loadAliases = () => api.get(`/ms/item-code-aliases`)
    .then((r) => {
      const m = {};
      (r.data?.aliases || []).forEach((a) => { m[String(a.wrong_code).toUpperCase()] = a.canon_code; });
      setAliasMap(m);
    }).catch(() => {});

  useEffect(() => {
    api.get(`/ms/pos/${pono}`).then((r) => setPo(r.data));
    loadAliases();
  }, [pono]);

  const startEdit = (i, cur) => { setEditingLine(i); setEditDate(isoToInput(cur)); };
  const cancelEdit = () => { setEditingLine(null); setEditDate(""); };
  const saveDue = async (i) => {
    setSaving(true);
    try {
      const r = await api.patch(`/ms/pos/${po.ms_pono}/line-due-date`, {
        line_index: i, deliv_date: editDate || null,
      });
      setPo(r.data);
      setEditingLine(null);
      toast.success("Due date updated. Re-run Transfer to push it to the replica.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update due date");
    } finally { setSaving(false); }
  };

  const startItemEdit = (i, cur) => { setEditingItem(i); setItemQuery(cur || ""); setItemResults([]); };
  const cancelItemEdit = () => { setEditingItem(null); setItemQuery(""); setItemResults([]); };
  const runItemSearch = async (q) => {
    setItemQuery(q);
    if ((q || "").trim().length < 2) { setItemResults([]); return; }
    setItemSearching(true);
    try {
      const r = await api.get(`/ms/item-master/search`, { params: { q } });
      setItemResults(r.data?.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Item search failed (ERP unreachable?)");
    } finally { setItemSearching(false); }
  };
  const saveItemCode = async (i, canonIcode) => {
    setSavingItem(true);
    try {
      const r = await api.patch(`/ms/pos/${po.ms_pono}/line-item-code`, {
        line_index: i, canon_icode: canonIcode,
      });
      setPo(r.data);
      await loadAliases();
      cancelItemEdit();
      const al = r.data?.item_alias;
      toast.success(al ? `Resolved ${al.wrong} → ${al.canon} everywhere.` : "Item code resolved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resolve item code");
    } finally { setSavingItem(false); }
  };

  if (!po) return <Layout breadcrumb={["Home", "POs", pono]}>Loading…</Layout>;

  return (
    <Layout breadcrumb={["Home", "POs", `PO ${pono}`]}>
      <div className="jde-page">
        <div className="jde-toolbar" data-testid="po-detail-toolbar">
          <Link to="/pos" className="jde-btn jde-btn-sm" data-testid="back-to-pos">
            <ArrowLeft size={12} weight="bold" /> Back
          </Link>
          <div style={{ marginLeft: 12, fontFamily: "var(--jde-font-mono)", fontSize: 13 }}>
            <strong>PO {po.ms_pono}</strong>
            <span className="muted" style={{ marginLeft: 12 }}>Cust PO: {po.ms_custpo || "—"}</span>
            <span className="muted" style={{ marginLeft: 12 }}>Customer: <strong>{po.customer_name}</strong></span>
            {po.rev_no > 0 && <span className="jde-badge partial" style={{ marginLeft: 12 }}>Rev R{po.rev_no}</span>}
            <span className={`jde-badge ${po.status === "closed" ? "shipped" : po.status === "partially_shipped" ? "partial" : "pending"}`} style={{ marginLeft: 8 }}>
              {po.status}
            </span>
          </div>
        </div>

        {/* SUMMARY TILES */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
          <Tile label="Ordered Qty" value={fmtNum(po.qty_ordered_total)} />
          <Tile label="Shipped Qty" value={fmtNum(po.qty_shipped_total)} color="#0F766E" />
          <Tile label="Pending Qty" value={fmtNum(po.qty_pending_total)} color="#B91C1C" />
          <Tile label="PO Amount" value={fmtCur(po.amount, po.currency_code)} />
        </div>

        <div className="jde-tab-strip">
          <button className={`jde-tab ${tab === "header" ? "active" : ""}`} onClick={() => setTab("header")} data-testid="tab-header">
            <FileText size={12} weight="bold" /> Header
          </button>
          <button className={`jde-tab ${tab === "lines" ? "active" : ""}`} onClick={() => setTab("lines")} data-testid="tab-lines">
            Line Items ({po.lines?.length || 0})
          </button>
          <button className={`jde-tab ${tab === "invoices" ? "active" : ""}`} onClick={() => setTab("invoices")} data-testid="tab-invoices">
            <Truck size={12} weight="bold" /> Dispatches ({po.related_invoices?.length || 0})
          </button>
          <button className={`jde-tab ${tab === "revisions" ? "active" : ""}`} onClick={() => setTab("revisions")} data-testid="tab-revisions">
            <ClockCounterClockwise size={12} weight="bold" /> Revisions ({po.rev_history?.length || 0})
          </button>
        </div>

        <div className="jde-panel">
          {tab === "header" && (
            <div className="jde-panel-body">
              <table className="jde-detail-grid">
                <tbody>
                  <Row k="MS SQL PO #" v={po.ms_pono} />
                  <Row k="Customer PO #" v={po.ms_custpo} />
                  <Row k="Customer" v={po.customer_name} />
                  <Row k="PO Date" v={fmtDate(po.po_date)} />
                  <Row k="Customer PO Date" v={fmtDate(po.cust_po_date)} />
                  <Row k="Revision" v={po.rev_no > 0 ? `R${po.rev_no} on ${fmtDate(po.rev_date)} (Rev: ${po.rev || "—"})` : "Original"} />
                  <Row k="Currency" v={po.currency || "—"} />
                  <Row k="PO Amount" v={fmtCur(po.amount, po.currency_code)} />
                  <Row k="Pallet/Box" v={po.pallet || "—"} />
                  <Row k="PO File" v={po.po_file || "—"} />
                  <Row k="Status" v={po.status} />
                  <Row k="Last sync from SAP" v={po.ms_sync_at ? fmtDateTime(po.ms_sync_at) : "—"} />
                </tbody>
              </table>
            </div>
          )}

          {tab === "lines" && (
            <div className="jde-panel-body" style={{ padding: 0 }}>
              <table className="jde-grid" data-testid="lines-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item Code</th>
                    <th>Description</th>
                    <th style={{ textAlign: "right" }}>Qty Ordered</th>
                    <th style={{ textAlign: "right" }}>Qty Shipped</th>
                    <th style={{ textAlign: "right" }}>Qty Open</th>
                    <th style={{ textAlign: "right" }}>Price</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Currency</th>
                    <th>Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(po.lines || []).map((ln, i) => (
                    <tr key={i} data-testid={`line-${i}`}>
                      <td className="text-mono">{ln.item_no ?? i + 1}</td>
                      <td className="text-mono" style={{ verticalAlign: "top", minWidth: 180 }}>
                        {editingItem === i ? (
                          <div data-testid={`item-edit-${i}`} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                              <input autoFocus value={itemQuery} onChange={(e) => runItemSearch(e.target.value)}
                                     placeholder="Search item # / name / RMP…" data-testid={`item-search-${i}`}
                                     style={{ fontSize: 11, padding: "3px 6px", width: 190 }} />
                              <button className="jde-btn jde-btn-sm" disabled={savingItem} onClick={cancelItemEdit}
                                      data-testid={`item-cancel-${i}`} title="Cancel"><X size={12} weight="bold" /></button>
                            </div>
                            <div style={{ border: "1px solid var(--jde-border)", borderRadius: 4, maxHeight: 180, overflowY: "auto", background: "white", width: 230 }}>
                              {itemSearching && <div className="muted" style={{ padding: 6, fontSize: 11 }}>Searching ERP…</div>}
                              {!itemSearching && itemQuery.trim().length < 2 && <div className="muted" style={{ padding: 6, fontSize: 11 }}>Type at least 2 characters.</div>}
                              {!itemSearching && itemQuery.trim().length >= 2 && itemResults.length === 0 && <div className="muted" style={{ padding: 6, fontSize: 11 }}>No matches in the item master.</div>}
                              {itemResults.map((r) => (
                                <button key={r.icode} data-testid={`item-opt-${r.icode}`} disabled={savingItem}
                                        onClick={() => saveItemCode(i, r.icode)}
                                        style={{ display: "block", width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid #f1f5f9", background: r.icode === ln.icode ? "#eef2ff" : "white", cursor: "pointer", padding: "5px 7px", fontSize: 11 }}>
                                  <strong style={{ fontFamily: "var(--jde-font-mono)" }}>{r.icode}</strong>
                                  {r.rmpcode ? <span className="muted" style={{ marginLeft: 6 }}>({r.rmpcode})</span> : null}
                                  <div className="muted" style={{ fontSize: 10 }}>{r.iname}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                            {aliasMap[String(ln.icode || "").toUpperCase()] ? (
                              <span data-testid={`item-aliased-${i}`}>
                                <span style={{ textDecoration: "line-through", color: "#94a3b8" }}>{ln.icode}</span>
                                <span style={{ margin: "0 4px", color: "#94a3b8" }}>→</span>
                                <strong>{aliasMap[String(ln.icode).toUpperCase()]}</strong>
                                <WmPart icode={aliasMap[String(ln.icode).toUpperCase()]} />
                              </span>
                            ) : (
                              <span>{ln.icode}<WmPart icode={ln.icode} /></span>
                            )}
                            {(
                              <button onClick={() => startItemEdit(i, ln.icode)} data-testid={`item-edit-btn-${i}`}
                                      title="Resolve to the correct item code (fixes everywhere)"
                                      style={{ border: "none", background: "none", cursor: "pointer", color: "var(--omh-primary, #4F46E5)", padding: 0, display: "inline-flex" }}>
                                <PencilSimple size={13} weight="bold" />
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                      <td>{ln.name}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(ln.qty_ordered)}</td>
                      <td className="text-mono" style={{ textAlign: "right", color: "#0F766E" }}>{fmtNum(ln.qty_shipped)}</td>
                      <td className="text-mono" style={{ textAlign: "right", fontWeight: 700, color: ln.qty_open > 0 ? "#B91C1C" : "#10B981" }}>{fmtNum(ln.qty_open)}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{fmtRate(ln.price, po.currency_code)}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{fmtCur(ln.amt, po.currency_code)}</td>
                      <td className="text-mono">{ln.currency || "—"}</td>
                      <td className="text-mono" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                        {editingLine === i ? (
                          <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                            <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)}
                                   data-testid={`due-input-${i}`} style={{ fontSize: 11, padding: "2px 4px" }} />
                            <button className="jde-btn jde-btn-sm" disabled={saving} onClick={() => saveDue(i)}
                                    data-testid={`due-save-${i}`} title="Save"><Check size={12} weight="bold" /></button>
                            <button className="jde-btn jde-btn-sm" disabled={saving} onClick={cancelEdit}
                                    data-testid={`due-cancel-${i}`} title="Cancel"><X size={12} weight="bold" /></button>
                          </span>
                        ) : (
                          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            {ln.deliv_date ? fmtDate(ln.deliv_date) : <span className="muted">— none —</span>}
                            <button onClick={() => startEdit(i, ln.deliv_date)} data-testid={`due-edit-${i}`}
                                    title="Edit due date"
                                    style={{ border: "none", background: "none", cursor: "pointer", color: "var(--omh-primary, #4F46E5)", padding: 0, display: "inline-flex" }}>
                              <PencilSimple size={13} weight="bold" />
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "invoices" && (
            <div className="jde-panel-body" style={{ padding: 0 }}>
              <table className="jde-grid">
                <thead>
                  <tr><th>Sale #</th><th>Invoice #</th><th>Date</th><th>Plant</th><th>Vehicle</th><th style={{ textAlign: "right" }}>G Total</th><th></th></tr>
                </thead>
                <tbody>
                  {(po.related_invoices || []).map((inv) => (
                    <tr key={inv.ms_sale_no} data-testid={`po-invoice-${inv.ms_sale_no}`}>
                      <td className="text-mono">{inv.ms_sale_no}</td>
                      <td className="text-mono">{inv.ms_inv_no || "—"}</td>
                      <td className="text-mono" style={{ fontSize: 11 }}>{fmtDate(inv.sale_date)}</td>
                      <td><span className="jde-badge confirmed">{inv.comp_code || "—"}</span></td>
                      <td className="text-mono">{inv.veh_no || "—"}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{fmtCur(inv.gtotal, po.currency_code)}</td>
                      <td><Link to={`/dispatch/${inv.ms_sale_no}`} style={{ color: "var(--omh-primary)", fontSize: 11 }}>open ↗</Link></td>
                    </tr>
                  ))}
                  {(po.related_invoices || []).length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: "center", padding: 20 }} className="muted">No dispatches yet for this PO.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "revisions" && (
            <div className="jde-panel-body" style={{ padding: 0 }}>
              {(po.rev_history || []).length === 0 ? (
                <div style={{ padding: 20, textAlign: "center" }} className="muted">No prior revisions. This is the original PO.</div>
              ) : (
                <table className="jde-grid">
                  <thead><tr><th>Rev #</th><th>Snapshotted at</th><th style={{ textAlign: "right" }}>Lines</th><th style={{ textAlign: "right" }}>Total Qty</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
                  <tbody>
                    {po.rev_history.map((rh, i) => (
                      <tr key={i}>
                        <td className="text-mono">R{rh.rev_no ?? 0}</td>
                        <td className="text-mono" style={{ fontSize: 11 }}>{rh.snapshot_at ? fmtDateTime(rh.snapshot_at) : "—"}</td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{(rh.lines || []).length}</td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(rh.qty_ordered_total)}</td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{fmtCur(rh.amount, po.currency_code)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Tile({ label, value, color }) {
  return (
    <div style={{ background: "white", border: "1px solid var(--jde-border)", padding: "10px 12px", borderRadius: 4 }}>
      <div className="muted" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--jde-font-mono)", color: color || "#0F172A", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <tr>
      <td className="muted" style={{ width: 220, fontSize: 11, fontWeight: 600 }}>{k}</td>
      <td className="text-mono" style={{ fontSize: 12 }}>{v ?? "—"}</td>
    </tr>
  );
}
